import React from "react";
import { ArrowClockwise, ArrowSquareOut, CheckCircle, Circle, EnvelopeSimple, XCircle } from "@phosphor-icons/react";
import { aud, btnGhost } from "../ui";
import { fmtDate } from "../assess/parts";
import { ApprovalPill, EmailStatusPill, H, STEP_ORDER } from "./parts";

const detail = (name, r) => {
  if (name === "w2_columns") return "Approval number added, Claim Status set to Approved";
  if (!r) return null;
  if (name === "allocate") return r.otma;
  if (name === "w2_group") return `Moved to ${r.group}`;
  if (name === "register") return r.reused ? "Row was already there, so it was kept" : `Row added${r.dealer ? ` · ${r.dealer}` : ""}`;
  if (name === "emails") return `${r.emails?.length || 0} draft${r.emails?.length === 1 ? "" : "s"} ready`;
  return null;
};

function Steps({ a }) {
  return (
    <ol className="space-y-2" data-testid="wi-approval-steps">
      {STEP_ORDER.map((k) => {
        const s = a.steps?.[k];
        const Icon = s?.status === "done" ? CheckCircle : s?.status === "failed" ? XCircle : Circle;
        const tone = s?.status === "done" ? "text-[#73bf44]" : s?.status === "failed" ? "text-red-400" : "text-neutral-600";
        return (
          <li key={k} className="flex gap-2.5 text-sm" data-testid={`wi-approval-step-${k}`}>
            <Icon size={18} weight={s ? "fill" : "regular"} className={`${tone} shrink-0`} />
            <div><div className="text-neutral-200">{a.step_labels?.[k] || k}</div>
              <div className={`text-xs ${s?.status === "failed" ? "text-red-300" : "text-neutral-500"}`}>{s?.status === "failed" ? s.error : s?.status === "done" ? detail(k, s.result) || "Done" : "Not done yet"}</div></div>
          </li>
        );
      })}
    </ol>
  );
}

function PartsOrdered({ e }) {
  if (e.template !== "parts_request") return null;
  const p = e.parts_ordered || {};
  const [cls, text] = p.status === "done" ? ["text-[#a6e07f]", `Monday: Parts Status set to Ordered, Parts Ordered ${new Date(`${p.date}T00:00:00`).toLocaleDateString("en-AU", { dateStyle: "medium" })}`]
    : p.status === "skipped" ? ["text-neutral-400", p.note]
    : p.status === "failed" ? ["text-red-300", `Couldn't update Monday yet: ${p.error}`]
    : ["text-neutral-500", "Monday's Parts Ordered date is filled in once this email is sent"];
  return <div className={`text-xs ${cls}`} data-testid="wi-email-parts-ordered">{text}</div>;
}

function Emails({ items, onView }) {
  if (!items?.length) return null;
  return (
    <div data-testid="wi-approval-emails"><H>Emails</H>
      <ul className="space-y-2">
        {items.map((e) => (
          <li key={e.id} className="border border-neutral-800 rounded-xl px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`wi-approval-email-${e.template}`}>
            <div className="min-w-0 space-y-0.5"><div className="text-sm text-neutral-200 flex items-center gap-2"><EnvelopeSimple size={15} className="text-[#73bf44]" />{e.label} <EmailStatusPill status={e.status} /></div>
              <div className="text-xs text-neutral-500 truncate">To {e.to?.join(", ") || "no address yet"}{e.cc?.length ? ` · CC ${e.cc.join(", ")}` : ""}{e.sent_at ? ` · sent ${fmtDate(e.sent_at)}` : ""}</div>
              {e.status === "failed" && <div className="text-xs text-red-300">{e.error}</div>}
              {e.send_error && e.status !== "sent" && <div className="text-xs text-amber-300" data-testid={`wi-email-send-error-${e.template}`}>{e.send_error}</div>}
              <PartsOrdered e={e} /></div>
            <div className="flex gap-2">
              {e.web_link && e.status !== "sent" && <a href={e.web_link} target="_blank" rel="noreferrer" className={btnGhost} data-testid={`wi-email-outlook-link-${e.template}`}><ArrowSquareOut size={14} /> Open draft</a>}
              <button className={btnGhost} onClick={() => onView(e)} data-testid={`wi-email-view-${e.template}`}>View & copy</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ApprovalResult({ a, onRetry, busy, onViewEmail, mondayUrl }) {
  return (
    <div className="space-y-5" data-testid="wi-approval-result">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div><div className="font-mono text-3xl font-bold text-white" data-testid="wi-approval-otma">{a.otma || "No number yet"}</div>
          <div className="text-xs text-neutral-500 mt-1" data-testid="wi-approval-by">{aud(a.amount, 2)} · {a.by_ai ? `approved by the AI (${a.ai_confidence}% confidence), recorded under ${a.approved_by?.name}` : `approved by ${a.approved_by?.name}`} · {fmtDate(a.created_at)}</div></div>
        <ApprovalPill status={a.status} testId="wi-approval-status" />
      </div>
      {a.status === "failed" && (
        <div className="border border-red-500/40 bg-red-500/[0.06] rounded-xl p-4 space-y-3" data-testid="wi-approval-error">
          <p className="text-sm text-red-300">{a.error}</p>
          <p className="text-xs text-neutral-400">Finished steps aren't repeated, and the approval keeps the same number when you retry.</p>
          <button className={btnGhost} onClick={onRetry} disabled={busy} data-testid="wi-approval-retry-btn"><ArrowClockwise size={14} className={busy ? "animate-spin" : ""} /> Retry</button>
        </div>
      )}
      <Steps a={a} />
      <Emails items={a.emails} onView={onViewEmail} />
      {a.rectification && <div><H>Approved work</H><p className="text-xs text-neutral-300 whitespace-pre-wrap">{a.rectification}</p></div>}
      {mondayUrl && <a href={mondayUrl} target="_blank" rel="noreferrer" className={btnGhost} data-testid="wi-approval-monday-link"><ArrowSquareOut size={14} /> Open in Monday.com</a>}
    </div>
  );
}
